import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Search, BookOpen, Lightbulb, Twitter, Linkedin, Copy, Check } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import FAQSchema from '../components/FAQSchema';
import V2Hero from '../components/v2/V2Hero';
import Section from '../components/v2/Section';
import { PrimaryCTA, CTABlock } from '../components/v2/CTAButton';
import { HERO } from '../data/heroImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=400&fit=crop';

const blogFaqs = [
  {
    question: "How do I get seen by the right customers online?",
    answer:
      "Consistent, keyword-led content published across your own website, YouTube, and podcast — combined with search-optimised show notes and blog posts — is the fastest way to be seen by the right people. Our Pro Plan builds this weekly content engine for you.",
  },
  {
    question: "What actually moves the needle on Google in 2026?",
    answer:
      "Original, expertise-driven content that answers real customer questions, technical SEO (Core Web Vitals, schema, sitemap hygiene), and a steady drumbeat of new pages on your own domain. Social alone won't do it.",
  },
  {
    question: "Do I need a blog if I already have a podcast?",
    answer:
      "Yes. Blog posts anchor podcast episodes on your own domain, which is what Google can crawl and rank. A podcast without written show notes and blog posts loses the majority of its SEO value.",
  },
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [copiedTip, setCopiedTip] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog`);
      if (Array.isArray(response.data)) setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTips = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tips/feed?limit=20`);
      setTips(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching tips:', error);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchTips();
  }, [fetchPosts, fetchTips]);

  const allTags = [...new Set(posts.flatMap((p) => p.tags || []))].sort();
  const tagCounts = {};
  posts.forEach((p) => (p.tags || []).forEach((t) => (tagCounts[t] = (tagCounts[t] || 0) + 1)));

  const filteredPosts = posts.filter((p) => {
    const matchesSearch =
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.excerpt || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = !selectedTag || (p.tags || []).some((t) => t.toLowerCase() === selectedTag.toLowerCase());
    return matchesSearch && matchesTag;
  });

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleCopyTip = async (tipText, tipId) => {
    try {
      await navigator.clipboard.writeText(`${tipText} — Audio & Co®`);
      setCopiedTip(tipId);
      setTimeout(() => setCopiedTip(null), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const featured = filteredPosts[0];
  const rest = filteredPosts.slice(1);

  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Podcast, SEO & Publishing Blog | Audio & Co."
        description="Practical guides on podcast management, SEO websites, YouTube and book publishing for UK coaches, consultants and authors."
        keywords={['seo tips uk', 'website optimisation', 'content marketing small business', 'podcast production uk', 'youtube growth', 'ai search visibility']}
        url="https://audioandco.com/blog"
      />
      <FAQSchema faqs={blogFaqs} />

      {/* HERO */}
      <V2Hero
        image={HERO.creatorLaptop}
        testId="blog-hero"
      >
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">
            The Audio &amp; Co. Blog
          </p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            Straight-talking guides on getting <span className="text-[#BC516F]">seen and heard</span>.
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            SEO, websites, podcasts, YouTube, AI search visibility. Practical writing for small business
            owners who don&apos;t have time for jargon. New posts weekly.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
            <CTABlock size="lg" testIdPrefix="blog-hero-cta" />
            <div className="text-sm text-[#475569]">
              {posts.length}+ evergreen guides · updated weekly
            </div>
          </div>
        </div>
      </V2Hero>

      {/* SEARCH + TAG FILTER */}
      <Section bg="white" testId="blog-filters" innerClassName="!py-8 sm:!py-10">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <input
              type="text"
              placeholder="Search articles…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[10px] text-[#1E2A44] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#BC516F] focus:ring-2 focus:ring-[#BC516F]/20 transition"
              data-testid="blog-search"
            />
          </div>
          <div className="lg:w-72">
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="w-full px-4 py-3 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[10px] text-[#1E2A44] focus:outline-none focus:border-[#BC516F] focus:ring-2 focus:ring-[#BC516F]/20 transition"
              data-testid="blog-filter"
            >
              <option value="">All categories ({posts.length})</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag} ({tagCounts[tag] || 0})
                </option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      {/* POSTS */}
      <Section bg="bg" testId="blog-list">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#BC516F]" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="font-display text-xl text-[#1E2A44] font-semibold mb-2">
              No articles found
            </h3>
            <p className="text-[#64748B]">Try a different search or clear the filter.</p>
          </div>
        ) : (
          <>
            {/* FEATURED POST */}
            {featured && (
              <article
                className="bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_10px_30px_rgba(30,42,68,0.06)] mb-12"
                data-testid="blog-featured"
              >
                <div className="md:grid md:grid-cols-2">
                  <div className="relative aspect-[16/10] md:aspect-auto md:h-full overflow-hidden">
                    <img
                      src={featured.featured_image}
                      alt={featured.image_alt || featured.title}
                      title={featured.image_title || featured.title}
                      className="w-full h-full object-cover"
                      loading="eager"
                      decoding="async"
                      onError={(e) => {
                        e.target.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="p-7 sm:p-9 flex flex-col">
                    <div className="inline-flex self-start items-center gap-1.5 bg-[#BC516F]/10 text-[#BC516F] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                      Featured
                    </div>
                    <h2 className="font-display font-bold text-[#1E2A44] text-2xl sm:text-3xl leading-tight mt-4">
                      {featured.title}
                    </h2>
                    <p className="text-[#475569] mt-3 leading-relaxed line-clamp-4">
                      {featured.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-[#64748B] mt-5">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> {formatDate(featured.created_at)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5" /> {featured.author || 'Jo Day'}
                      </span>
                    </div>
                    <div className="mt-6">
                      <Link
                        to={`/blog/${featured.slug}`}
                        className="inline-flex items-center gap-2 font-semibold text-white bg-[#BC516F] hover:bg-[#9A3A54] rounded-[10px] shadow-[0_10px_30px_rgba(188,81,111,0.25)] hover:shadow-[0_14px_36px_rgba(154,58,84,0.35)] transition px-6 py-3 text-base"
                        data-testid="blog-featured-cta"
                      >
                        Read full article <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            )}

            {/* GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post, idx) => (
                <Link
                  key={post.id || post.slug || idx}
                  to={`/blog/${post.slug}`}
                  className="group bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_4px_16px_rgba(30,42,68,0.04)] hover:shadow-[0_14px_36px_rgba(30,42,68,0.10)] hover:-translate-y-0.5 transition-all"
                  data-testid={`blog-post-${idx}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#F6F8FB]">
                    <img
                      src={post.featured_image}
                      alt={post.image_alt || post.title}
                      title={post.image_title || post.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="p-6">
                    {(post.tags || []).slice(0, 1).map((tag) => (
                      <span
                        key={tag}
                        className="inline-block text-xs font-semibold uppercase tracking-wider text-[#BC516F] mb-2"
                      >
                        {tag}
                      </span>
                    ))}
                    <h3 className="font-display font-semibold text-[#1E2A44] text-lg leading-snug line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-[#64748B] text-sm mt-2 line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#E2E8F0]">
                      <span className="text-xs text-[#64748B] inline-flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" /> {formatDate(post.created_at)}
                      </span>
                      <span className="text-sm font-semibold text-[#1E2A44] inline-flex items-center gap-1 group-hover:text-[#BC516F] transition">
                        Read <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </Section>

      {/* TIPS FEED */}
      {tips.length > 0 && (
        <Section bg="white" testId="tips-feed">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#BC516F]/10 text-[#BC516F] mb-4">
              <Lightbulb className="w-5 h-5" />
            </div>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">
              Jo&apos;s tips &amp; quick wins
            </h2>
            <p className="text-[#64748B] mt-3">Small, actionable ideas — one at a time.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {tips.map((tip, index) => (
              <div
                key={tip._id || index}
                className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6 flex flex-col"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#BC516F]/10 text-[#BC516F] text-xs px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider">
                    {tip.category}
                  </span>
                  {tip.week_number && (
                    <span className="text-xs text-[#94A3B8]">Week {tip.week_number}</span>
                  )}
                </div>
                <p className="text-[#1E2A44] font-medium leading-relaxed">&ldquo;{tip.tip}&rdquo;</p>
                {tip.action && (
                  <p className="text-sm text-[#64748B] mt-3 leading-relaxed">
                    <strong className="text-[#1E2A44]">Action:</strong> {tip.action}
                  </p>
                )}
                <div className="flex items-center gap-1 mt-4 pt-4 border-t border-[#E2E8F0]">
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                      tip.tip + ' — @AudioAndCo'
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#94A3B8] hover:text-[#1E2A44] transition"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                  <a
                    href="https://www.linkedin.com/sharing/share-offsite/?url=https://audioandco.com/blog"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#94A3B8] hover:text-[#1E2A44] transition"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleCopyTip(tip.tip, tip._id)}
                    className="p-2 text-[#94A3B8] hover:text-[#1E2A44] transition"
                    aria-label="Copy tip"
                  >
                    {copiedTip === tip._id ? (
                      <Check className="w-4 h-4 text-[#BC516F]" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* FINAL CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            Reading won&apos;t make you visible. Publishing will.
          </h2>
          <p className="text-white/75 mt-4 text-lg">
            The Pro Plan turns every recording into 12 published outputs a week.
          </p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="blog-final-cta" />
          </div>
        </div>
      </Section>
    </main>
  );
}
